import React from 'react';
import { Theme } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import createStyles from '@mui/styles/createStyles';
import { clsx } from 'clsx';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
// Components
import ModalHeader, { ModalHeaderProps } from './templates/modalHeader';
// Resources
import { isWebView } from '../../utils/webview';
import { getBottomBarHeight } from '../../localStorageAPI';

type ComponentType<T> = React.ComponentClass<T> | React.FunctionComponent<T>;

export interface ModalHocProps {
    isOpen: boolean;
    onRequestClose: () => void;
    modalSize?: ModalSize;
    title?: string;
    label?: string;
    subtitle?: string;
    description?: string;
    modalPosition?: 'left' | 'right' | 'bottom';
    modalStyle?: any;
    withHeader?: boolean;
    isAddress?: boolean;
    classes?: any;
    children?: any;
    canEscape?: boolean;
    cancelActionOnCrossClose?: boolean;
    floatingClose?: ModalHeaderProps['floatingClose'];
}

export enum ModalSize {
    xs = 'xs',
    sm = 'sm',
    md = 'md',
    lg = 'lg',
    xl = 'xl',
}

export interface PropsFromModal {
    onRequestClose: () => void;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function createModal<ComponentProps>(Component: ComponentType<ComponentProps>) {
    type MergedProps = ComponentProps & ModalHocProps;

    class ModalHoc extends React.Component<MergedProps> {
        // eslint-disable-next-line react/static-property-placement
        static displayName = `ModalHoc(${Component.displayName || Component.name})`;

        getModalWidth = (classes) => {
            const { modalSize } = this.props;
            switch (modalSize) {
                case ModalSize.xs:
                    return classes.xs;
                case ModalSize.sm:
                    return classes.sm;
                case ModalSize.md:
                    return classes.md;
                case ModalSize.lg:
                    return classes.lg;
                case ModalSize.xl:
                    return classes.xl;
                default:
                    return classes.xl;
            }
        };

        onDialogRequestClose = () => {
            const { canEscape } = this.props;
            if (canEscape) {
                this.onDialogCrossClose();
            }
        };

        onDialogCrossClose = () => {
            this.props.onRequestClose();
            const { cancelActionOnCrossClose } = this.props;
            if (cancelActionOnCrossClose) {
                (this.props as any).onCancelAction?.();
            }
        };

        render() {
            const {
                isOpen,
                classes,
                modalSize,
                modalPosition,
                title,
                label,
                subtitle,
                withHeader = true,
                isAddress,
                modalStyle,
                children,
                floatingClose = 'none',
            } = this.props;

            // The Android WebView is laid out edge to edge, so its system bar sits on top of
            // whatever the modal puts last - usually the confirm button. Reserved here rather than
            // per caller, and inline because the height only reaches the webapp once the native app
            // has stored it. An anchored modal (a bottom sheet, or a full-height side panel) has to
            // pad its own content; a centered one only has to stop short of the bar.
            const bottomBarHeight = getBottomBarHeight();
            const paperStyle: React.CSSProperties = !bottomBarHeight
                ? null
                : modalPosition
                  ? { paddingBottom: MODAL_PADDING + bottomBarHeight }
                  : {
                        marginBottom: DIALOG_MARGIN + bottomBarHeight,
                        maxHeight: `calc(100% - ${2 * DIALOG_MARGIN + bottomBarHeight}px)`,
                    };

            return (
                <Dialog
                    open={isOpen}
                    onClose={this.onDialogRequestClose}
                    fullWidth={!modalSize}
                    maxWidth={modalSize ?? false}
                    closeAfterTransition
                    className={modalPosition ? classes[modalPosition] : null}
                    PaperProps={paperStyle ? { style: paperStyle } : undefined}
                    classes={{
                        paper: clsx(
                            classes.dialog,
                            this.getModalWidth(classes),
                            modalStyle,
                            isAddress && classes.address,
                        ),
                    }}
                >
                    <>
                        {withHeader && title && (
                            <ModalHeader
                                title={title}
                                label={label}
                                subtitle={subtitle}
                                onClose={this.onDialogCrossClose}
                                floatingClose={floatingClose}
                            />
                        )}
                        {children ? (
                            <>{React.cloneElement(children, { ...this.props })}</>
                        ) : (
                            <DialogContent style={dialogStyles} className="dialogContentModal">
                                <Component {...(this.props as any)} />
                            </DialogContent>
                        )}
                    </>
                </Dialog>
            );
        }
    }

    return withStyles(styles)(ModalHoc);
}

export default createModal;

const isWebview = isWebView();
const MODAL_PADDING = 30;
// Margin MUI keeps around a centered dialog, and which caps its height at `100% - 2 * margin`.
const DIALOG_MARGIN = 32;
const dialogStyles: React.CSSProperties = { margin: 0, padding: 0 };
const styles = (theme: Theme) =>
    createStyles({
        '@global': {
            // need add into global rules
            '@keyframes slide': {
                '0%': {
                    right: -2000,
                },
                '100%': {
                    right: 0,
                },
            },
        },
        dialog: {
            borderRadius: 10,
            backgroundColor: theme.palette.colors.background.appBar,
            padding: MODAL_PADDING,
            '@media (max-width: 900px)': {
                maxWidth: 'none !important',
            },
            ...(isWebview && {
                '& .dialogContentModal': {
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    scrollbarWidth: 'none',
                    '-ms-overflow-style': 'none',
                },
            }),
        },
        xs: {
            maxWidth: '20%',
            minWidth: '10%',
        },
        sm: {
            maxWidth: '30%',
            minWidth: '30%',
        },
        md: {
            maxWidth: '60%',
            minWidth: '30%',
        },
        lg: {
            maxWidth: '75%',
            minWidth: '40%',
        },
        xl: {
            maxWidth: '90%',
            minWidth: '40%',
        },
        address: {
            width: '60%',
        },
        right: {
            animation: 'slide 0.5s forwards',
            '& .MuiPaper-root': {
                borderRadius: 0,
            },
            '& .MuiDialog-container': {
                width: '100%',
                justifyContent: 'flex-end',
                alignItems: 'normal',
            },
        },
        left: {
            animation: 'slide 0.5s forwards',
            '& .MuiPaper-root': {
                borderRadius: 0,
            },
            '& .MuiDialog-container': {
                width: '100%',
                justifyContent: 'flex-start',
                alignItems: 'normal',
            },
        },
        bottom: {
            '& .MuiPaper-root': {
                bottom: 0,
                height: 'fit-content',
                position: 'absolute',
            },
            '& .MuiDialog-container': {
                width: '100%',
                justifyContent: 'flex-start',
                alignItems: 'normal',
            },
        },
    });
